import { HttpClient } from '@angular/common/http';
import { Component, Inject, NgZone, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_VERSION, ComplaintV1, Transport, TransportMessage, TransportStatus, transportStatuses, TransportStatusesColors } from '@icc/b2b/shared';
import { ConfigService } from '@icc/shared/config.service';
import { parseDate } from '@icc/shared/helpers';
import { core, StatsService, ToastService } from '@icc/shared/helpers/b2b';
import { InputComponent } from '@icc/ui';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { ReplaySubject, Subscription } from 'rxjs';
import { Keyboard } from '@capacitor/keyboard';

@Component({
    selector: 'icc-transport',
    templateUrl: './transport.component.html',
    styleUrls: ['./transport.component.scss'],
})
export class TransportComponent {
    static crumbsSubject = new ReplaySubject<Record<string, any>>();

    openedTransportRequestId?: string;
    transportRequest?: Transport;

    hasAccessTo = {
        manage: false,
        pickUp: false,
    };

    saving = false;
    preview = false;

    editPickupDate = false;
    pickupDate: Date | null = null;

    editDriver = false;
    driverUserId: number | null = null;

    statusColorHex = 'ffffff';
    transportStatuses = transportStatuses;

    drivers: Record<number, {
        name: string;
        phoneNumber: string;
    }> = {};

    messages: TransportMessage[] = [];
    newMessage = '';
    keyboardOpened = false;

    deliveryAddresses: any[] = [];

    @ViewChild('pickupDateInput', { static: false }) pickupDateInput: InputComponent;

    private subscriptions: Subscription[] = [];

    constructor(
        private route: ActivatedRoute,
        private configService: ConfigService,
        private router: Router,
        private http: HttpClient,
        private translateService: TranslateService,
        private permissionsService: NgxPermissionsService,
        @Inject(APP_VERSION) private appVersion: string,
        private toastService: ToastService,
        private ngZone: NgZone,
        private statsService: StatsService,
    ) {}

    ngOnInit(): void {
        this.subscriptions.push(
            this.route.params.subscribe((params) => {
                if (params.id) {
                    this.openedTransportRequestId = params.id;
                    this.loadRequest();
                } else {
                    const complaintId = params.complaintId;
                    if (complaintId) {
                        this.createComplaintTransportRequest(complaintId);
                    } else {
                        this.createTransportRequest();
                    }
                }
                this.statsService.add({
                    action: 'open-transport',
                })
            }),
        );
        Keyboard.addListener('keyboardWillShow', () => {
            this.ngZone.run(() => {
                this.keyboardOpened = true;
            });
        });
        Keyboard.addListener('keyboardWillHide', () => {
            this.ngZone.run(() => {
                this.keyboardOpened = false;
            });
        });
        this.checkAccess();
        this.loadDrivers();
        this.loadDeliveryAddresses();
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        Keyboard.removeAllListeners();
    }

    async checkAccess() {
        this.hasAccessTo.manage = await this.permissionsService.hasPermission('trade-transport-manage');
        this.hasAccessTo.pickUp = await this.permissionsService.hasPermission('driver-transports') &&
            this.transportRequest?.status !== 'canceled';
    }

    async loadDrivers() {
        this.drivers = {};
        for (const driver of this.configService.user.drivers) {
            this.drivers[driver.id] = driver;
        }
    }

    loadDeliveryAddresses() {
        if (this.configService.user?.company?.addresses) {
            this.deliveryAddresses = this.configService.user.company.addresses.filter(el => Array.isArray(el?.contactFunctions) && el.contactFunctions?.includes('delivery'));
        } else {
            this.deliveryAddresses = [];
        }
    }

    async loadRequest() {
        const {transport} = await this.http.get<{
            transport: Transport
        }>(`/api/transport/transport/${this.openedTransportRequestId}`).toPromise();
        this.transportRequest = transport;
        this.pickupDate = this.transportRequest.pickupDate ? new Date(this.transportRequest.pickupDate) : null;
        this.driverUserId = this.transportRequest.driverUserId;
        this.statusColorHex = TransportStatusesColors[this.transportRequest.status];
        this.setDocumentCrumbs();
        this.loadMessages();
    }

    async createTransportRequest() {
        this.transportRequest = {
            uuid: core.generateUUID(),
            documentNumber: [],
            sellerCompanyId: this.configService.user.user.contractorCompanyId,
            driverUserId: null,
            contractorId: +this.configService.user.user.ActiveCompany,
            contractorUserId: this.configService.user.user.id,
            contractorName: this.configService.user.user?.person?.reference_name,
            description: '',
            pickupAddress: '',
            pickupPhoneNumber: '',
            pickupDate: null,
            isCollected: false,
            driverNote: '',
            status: 'requested',
            created: new Date(),
            modified: new Date(),
        };
        this.statusColorHex = TransportStatusesColors[this.transportRequest.status];
        this.setDocumentCrumbs();
    }

    async createComplaintTransportRequest(complaintId: string = null) {
        const {complaint, pickupAddress, pickupPhoneNumber} = await this.http.get<{
            complaint: ComplaintV1,
            pickupAddress: string,
            pickupPhoneNumber: string,
        }>(`/api/complaint-v1/complaint/${complaintId}`).toPromise();

        if (!complaint) {
            this.router.navigate(['transport', 'transports']);
            return;
        }

        this.transportRequest = {
            uuid: core.generateUUID(),
            documentNumber: [],
            complaintId: complaint.id,
            complaintDocumentNumber: complaint.documentNumber,
            sellerCompanyId: +this.configService.user.user.ActiveCompany,
            driverUserId: null,
            contractorId: complaint.contractorId,
            contractorUserId: complaint.contractorUserId,
            contractorName: complaint.contractorName,
            contractorCompanyName: complaint.contractorCompanyName,
            description: '',
            pickupAddress: pickupAddress || '',
            pickupPhoneNumber: pickupPhoneNumber || '',
            pickupDate: null,
            isCollected: false,
            driverNote: '',
            status: 'requested',
            created: new Date(),
            modified: new Date(),
        };
        this.statusColorHex = TransportStatusesColors[this.transportRequest.status];
        this.setDocumentCrumbs();
    }

    changePickupDate() {
        this.transportRequest.pickupDate = this.pickupDate ? parseDate(this.pickupDate) : null;

        this.editPickupDate = false;
        this.saveTransportRequest();
    }

    closeEditPickupDate() {
        this.editPickupDate = false;
    }

    changePickupAddress(address: string) {
        this.transportRequest.pickupAddress = address;
        this.saveTransportRequest();
    }

    changeDescription(description: string) {
        this.transportRequest.description = description;
    }

    changeStatus(status: TransportStatus) {
        this.transportRequest.status = status;
        this.statusColorHex = TransportStatusesColors[status];
        this.saveTransportRequest();
    }

    changeDriver() {
        this.transportRequest.driverUserId = this.driverUserId;
        this.editDriver = false;
        this.saveTransportRequest();
    }

    changeDriverNote(note: string) {
        this.transportRequest.driverNote = note;
        this.saveTransportRequest();
    }

    closeEditDriver() {
        this.driverUserId = this.transportRequest.driverUserId;
        this.editDriver = false;
    }

    focusInput(input: string) {
        if (input === 'pickupDate') {
            setTimeout(() => {
                this.pickupDateInput?.focus();
            });
        }
    }

    async sendTransportRequest() {
        await this.http.post('/api/transport/transport/create', {
            transport: this.transportRequest,
            userId: this.configService?.user?.user?.id,
            appVersion: this.appVersion,
        }).toPromise();
        this.router.navigate(['transport', 'transports']);
    }

    async saveTransportRequest() {
        await this.http.post('/api/transport/transport/' + this.transportRequest.uuid, {
            transport: this.transportRequest,
            userId: this.configService?.user?.user?.id,
            appVersion: this.appVersion,
        }).toPromise();
        this.toastService.add({
            title: this.translateService.instant('TRANSPORT|Zapisano zmiany'),
            body: this.translateService.instant('TRANSPORT|Zmiany zostały zapisane'),
        });
    }

    async collect() {
        this.transportRequest.isCollected = true;
        await this.http.post('/api/transport/transport/' + this.transportRequest.uuid + '/collect', {
            userId: this.configService?.user?.user?.id,
            appVersion: this.appVersion,
        }).toPromise();

        this.router.navigate(['transport', 'transports']);
    }

    async loadMessages() {
        if (!(await this.permissionsService.hasPermission(['trade-transport-manage', 'trade-request-transport']))) {
            return;
        }
        const {messages} = await this.http.get<{
            messages: TransportMessage[]
        }>(`/api/transport/transport/${this.openedTransportRequestId}/messages`).toPromise();
        this.messages = messages;
    }

    async sendMessage() {
        await this.http.post('/api/transport/transport/' + this.transportRequest.uuid + '/message', {
            message: this.newMessage,
            userId: this.configService?.user?.user?.id,
            appVersion: this.appVersion,
        }).toPromise();
        this.newMessage = '';
        this.loadMessages();
    }

    setDocumentCrumbs(){
        TransportComponent.crumbsSubject.next({
            level: 1,
            crumbs: [{
                label: this.translateService.instant('TRANSPORT|Lista zgłoszeń transportu'),
                url: '/transport/transports',
            }]
        });

        if(this.openedTransportRequestId){
            TransportComponent.crumbsSubject.next({
                level: 2,
                crumbs: [
                    {
                        label: this.translateService.instant('TRANSPORT|Zgłoszenie') + ' ' + (this.transportRequest?.documentNumber || []).join(''),
                        url: '/transport/transport/'+this.openedTransportRequestId,
                    }
                ],
            });
        } else {
            TransportComponent.crumbsSubject.next({
                level: 2,
                crumbs: [{
                    label: this.translateService.instant('TRANSPORT|Nowe zgłoszenie transportowe'),
                    url: '/transport/transport/new',
                }],
            });
        }

    }

}
